import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import React, { useState } from "react";
import LogoSvg from "../../assets/images/newlogo-primary.png";
import {
  MoreHoriz,
  MenuOpen,
  Menu,
  Store,
  AvTimer,
  SupportAgent,
  PeopleAlt,
  SpaceDashboard,
  ManageAccounts,
  SettingsSuggest,
  MeetingRoom,
  Mode,
  SettingsBrightness,
  Group,
  Receipt,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { logout } from "../../store/authSlice";
import ThemeToggle from "../buttons/ThemeToggle";

const SIDEBAR_WIDTH = 300;
const SIDEBAR_COLLAPSED_WIDTH = 72;

const SideBar = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [collapsed, setCollapsed] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const [links] = useState([
    {
      name: "Dashboard",
      icon: <SpaceDashboard />,
      link: "/",
    },
    {
      name: "Vendors",
      icon: <ManageAccounts />,
      link: "/vendors",
    },
    {
      name: "Stores",
      icon: <Store />,
      link: "/store",
    },
    {
      name: "Services",
      icon: <SettingsSuggest />,
      link: "/services",
    },
    {
      name: "Customers",
      icon: <PeopleAlt />,
      link: "/customers",
    },

    {
      name: "Delivery Slots",
      icon: <AvTimer />,
      link: "/delivery-slots",
    },

    {
      name: "Customer Queries",
      icon: <SupportAgent />,
      link: "/customer-queries",
    },
    {
      name: "Business Enquiries",
      icon: <Receipt />,
      link: "/business-enquiries",
    },
    // {
    //   name: "Status",
    //   icon: <MoreHoriz />,
    //   link: "/Status",
    // },
    {
      name: "Team Management",
      icon: <Group />,
      link: "/team-management",
    },
    // {
    //   name: "Transactions",
    //   icon: <Receipt />,
    //   link: "/transactions",
    // },
    {
      name: "Logout",
      icon: <MeetingRoom />,
    },
    {
      name: (
        <span style={{ display: "flex", gap: "16px", alignItems: "center" }}>
          <span>Preference:</span>
          <ThemeToggle />
        </span>
      ),
      icon: <SettingsBrightness />,
    },
  ]);

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = () => {
    setLogoutDialogOpen(false);
    navigate("/login");
    dispatch(logout());
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  return (
    <>
      <Paper
        elevation={3}
        sx={{
          width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
          overflow: "auto",
          borderRadius: 0,
          borderRight: `1px ${theme.palette.divider} solid`,
          transition: "width 0.2s",
          bgcolor: theme.palette.background.default,
          color: theme.palette.text.primary,
          position: "relative",
        }}
      >
        <Stack py={2} spacing={2}>
          <Box
            display="flex"
            alignItems="center"
            px={collapsed ? 1 : 2}
            justifyContent={collapsed ? "center" : "space-between"}
          >
            <Box display="flex" alignItems="center" gap={1}>
              {!collapsed && (
                <img
                  alt="brand trademark"
                  src={LogoSvg}
                  style={{
                    width: 40,
                    height: 40,
                    transition: "width 0.2s, height 0.2s",
                  }}
                />
              )}
              {!collapsed && (
                <Typography variant="h6" fontWeight={700} ml={1}>
                  Zerox Cloud
                </Typography>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={() => setCollapsed((prev) => !prev)}
              sx={{
                ml: collapsed ? 0 : 1,
                color: theme.palette.text.primary,
                bgcolor: theme.palette.action.selected,
                "&:hover": { bgcolor: theme.palette.action.hover },
                transition: "margin 0.2s",
              }}
            >
              {collapsed ? <Menu /> : <MenuOpen />}
            </IconButton>
          </Box>
          <Divider
            sx={{ bgcolor: theme.palette.divider, mx: collapsed ? 1 : 2 }}
          />
          <List sx={{ flex: 1, px: 1 }}>
            {links.map((link, index) => (
              <ListItem key={index} disablePadding sx={{ mb: 0.5 }}>
                <Tooltip
                  title={collapsed ? link.name : ""}
                  placement="right"
                  arrow
                >
                  <ListItemButton
                    selected={link.link === location.pathname}
                    onClick={() => {
                      if (link.name === "Logout") {
                        handleLogoutClick();
                      } else if (link.link) {
                        navigate(link.link);
                      }
                    }}
                    sx={{
                      minHeight: 48,
                      px: collapsed ? 1.5 : 3,
                      borderRadius: 2,
                      bgcolor:
                        link.link === location.pathname
                          ? theme.palette.action.selected
                          : "transparent",
                      "&:hover": {
                        bgcolor: theme.palette.action.hover,
                      },
                      transition: "background 0.2s",
                      justifyContent: collapsed ? "center" : "flex-start",
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: theme.palette.text.primary,
                        minWidth: 0,
                        mr: collapsed ? 0 : 2,
                        justifyContent: "center",
                      }}
                    >
                      {link.icon}
                    </ListItemIcon>
                    {!collapsed && (
                      <ListItemText
                        primary={link.name}
                        primaryTypographyProps={{
                          fontWeight:
                            link.link === location.pathname ? 700 : 500,
                          fontSize: 16,
                        }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </List>
          <Box height={24} />
        </Stack>
      </Paper>
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
      >
        <DialogTitle id="logout-dialog-title">Logout Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText id="logout-dialog-description">
            Are you sure you want to logout?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ mb: 2, px: 2 }}>
          <Button onClick={handleLogoutCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleLogoutConfirm} variant="contained" autoFocus>
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SideBar;
